// Main-thread wrapper around the validation worker. One worker per run, terminated on
// completion — if a persistent worker is ever needed, this seam becomes a
// persistent-worker protocol without anything else moving.

import {
  AnnotateWorkerInput,
  ExternalKeySetsWorkerInput,
  ValidationWorkerAnnotatedDone,
  ValidationWorkerDone,
  ValidationWorkerExternalKeySetsDone,
  ValidationWorkerInput,
  ValidationWorkerProgress,
  ValidationWorkerRequest,
  ValidationWorkerResponse,
} from "./workerProtocol";

export async function runValidationInWorker(
  input: ValidationWorkerInput,
  onProgress: (progress: ValidationWorkerProgress) => void
): Promise<ValidationWorkerDone> {
  const worker = new Worker(new URL("./validation.worker.ts", import.meta.url), { type: "module" });
  try {
    return await new Promise<ValidationWorkerDone>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<ValidationWorkerResponse>) => {
        const message = event.data;
        if (message.kind === "progress") onProgress(message);
        if (message.kind === "done") resolve(message);
        if (message.kind === "error") reject(new Error(message.message));
      };
      worker.onerror = (event) => reject(new Error(event.message || "Validation worker failed to start"));
      const request: ValidationWorkerRequest = { kind: "run", input };
      worker.postMessage(request);
    });
  } finally {
    worker.terminate();
  }
}

// Builds the annotated copy off the main thread — exceljs serialisation of a
// 12k-row workbook is no lighter than reading one. Same one-shot worker shape as above.
export async function runAnnotateInWorker(input: AnnotateWorkerInput): Promise<ValidationWorkerAnnotatedDone["files"]> {
  const worker = new Worker(new URL("./validation.worker.ts", import.meta.url), { type: "module" });
  try {
    return await new Promise<ValidationWorkerAnnotatedDone["files"]>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<ValidationWorkerResponse>) => {
        const message = event.data;
        if (message.kind === "annotatedDone") resolve(message.files);
        if (message.kind === "error") reject(new Error(message.message));
      };
      worker.onerror = (event) => reject(new Error(event.message || "Validation worker failed to start"));
      const request: ValidationWorkerRequest = { kind: "annotate", input };
      worker.postMessage(request);
    });
  } finally {
    worker.terminate();
  }
}

// Full CSV read of the largest Static Data sheets, so it stays off the main thread.
export async function runExternalKeySetsInWorker(
  input: ExternalKeySetsWorkerInput
): Promise<Omit<ValidationWorkerExternalKeySetsDone, "kind">> {
  const worker = new Worker(new URL("./validation.worker.ts", import.meta.url), { type: "module" });
  try {
    return await new Promise<Omit<ValidationWorkerExternalKeySetsDone, "kind">>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<ValidationWorkerResponse>) => {
        const message = event.data;
        if (message.kind === "externalKeySetsDone") {
          resolve({ keySets: message.keySets, missingWorksheets: message.missingWorksheets });
        }
        if (message.kind === "error") reject(new Error(message.message));
      };
      worker.onerror = (event) => reject(new Error(event.message || "Validation worker failed to start"));
      const request: ValidationWorkerRequest = { kind: "externalKeySets", input };
      worker.postMessage(request);
    });
  } finally {
    worker.terminate();
  }
}
